import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { ClearDeviceListDialogComponent } from "../clear-device-list-dialog/clear-device-list-dialog.component";
import { Device } from "../models/device";
import { NavigatorService } from "../services/engine/navigator.service";
import {
  ProvisionerService,
  WebSocketMessageInterface
} from "../services/engine/provisioner.service";
import { DeviceFlasherComponent } from "../device-flasher/device-flasher.component";
import { MatDivider } from "@angular/material/divider";
import { DeviceItemComponent } from "../device-item/device-item.component";
import { MatButton } from "@angular/material/button";

@Component({
  selector: "app-device-flash",
  templateUrl: "./device-flash.component.html",
  styleUrls: ["./device-flash.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DeviceFlasherComponent, MatDivider, DeviceItemComponent, MatButton]
})
export class DeviceFlashComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  timeoutId;

  isFlashing = true;
  isAssembling = false;
  isConfirming = false;
  isFinishedConfirming = false;
  isFinishedWithError = false;
  testEnabled = false;
  actionsEnabled = false;
  connectionError = false;
  waitingWebsocket = false;

  nbFlashedDevices = 0;
  webSocketLoading = 0;

  devices: Device[] = [];
  devicesWebsocket: Device[] = [];

  listDeviceType = "";
  alldissociated = false;

  constructor(
    private provisioning: ProvisionerService,
    private navigatorService: NavigatorService,
    public dialog: MatDialog
  ) {
    this.testEnabled = this.provisioning.canTestDevice();
    switch (this.provisioning.getStep()) {
      case ProvisionerService.flashingStep:
        this.isFlashing = true;
        this.isAssembling = false;
        this.isConfirming = false;
        this.isFinishedConfirming = false;
        this.isFinishedWithError = false;
        break;
      case ProvisionerService.provisionedStep:
        this.isFlashing = false;
        this.isAssembling = true;
        this.isConfirming = false;
        this.isFinishedConfirming = false;
        this.isFinishedWithError = false;
        break;
      case ProvisionerService.confirmedStep:
        this.isFlashing = false;
        this.isAssembling = false;
        this.isConfirming = false;
        this.isFinishedConfirming = true;
        this.isFinishedWithError = false;
        break;
      case ProvisionerService.confirmedErrorStep:
        this.isFlashing = false;
        this.isAssembling = false;
        this.isConfirming = false;
        this.isFinishedConfirming = true;
        this.isFinishedWithError = true;
        break;
      default:
        this.isFlashing = true;
        this.isAssembling = false;
        this.isConfirming = false;
        this.isFinishedConfirming = false;
        this.isFinishedWithError = false;
    }
  }

  ngOnInit() {
    this.listDeviceType = this.navigatorService.getCurrentMode();
    // console.log("DeviceFlashComponent - listDeviceType", this.listDeviceType);

    this.subscription.add(
      this.provisioning.deviceListWebsocket.subscribe(
        (webSocketMessageInterface: WebSocketMessageInterface) => {
          this.devicesWebsocket = webSocketMessageInterface.devices;
          this.webSocketLoading = webSocketMessageInterface.loading;
        }
      )
    );

    this.subscription.add(
      this.provisioning.deviceListEvent.subscribe((list: Device[]) => {
        console.log("deviceListEvent()", list);
        if (this.waitingWebsocket) {
          this.waitingWebsocket = false;
          this.webSocketLoading = 0;
          if (this.devicesWebsocket.length !== list.length)
            console.log(
              "an error occured for at least one device",
              this.devicesWebsocket,
              list
            );
        }
        this.provisioning.enableActions.emit(true);
        this.actionsEnabled = true;
        this.isConfirming = false;
        if (list) {
          this.connectionError = false;
          // this.devices = list.sort((a: Device, b: Device): number => {
          //   console.log("deviceListEvent()", sort);
          //   return a.order - b.order;
          // });
          this.devices = list;
          if (this.devices.length === 0) {
            this.isFlashing = true;
            this.isAssembling = false;
            this.isConfirming = false;
            this.isFinishedConfirming = false;
            this.isFinishedWithError = false;
            this.provisioning.saveStep(ProvisionerService.flashingStep);
          }

          if (this.provisioning.getStep() === ProvisionerService.flashingStep) {
            this.isFlashing = true;
            this.provisioning.saveStep(ProvisionerService.flashingStep);
          } else {
            this.isFlashing = false;

            if (this.isAssembly()) {
              this.isAssembling = true;
            }
          }
          this.nbFlashedDevices = list ? list.length : 0;
        } else {
          if (this.provisioning.getStep() === ProvisionerService.flashingStep) {
            this.connectionError = true;
            this.isFlashing = true;
            this.isAssembling = false;
            this.isConfirming = false;
            this.isFinishedConfirming = false;
            this.isFinishedWithError = true;
            this.provisioning.enableActions.emit(true);
          } else {
            this.connectionError = true;
            this.provisioning.enableActions.emit(true);
            this.actionsEnabled = true;
            this.isFinishedConfirming = true;
            this.isConfirming = false;
            this.isFinishedWithError = true;
          }
        }
      })
    );

    this.subscription.add(
      this.provisioning.executeUnprovisioning.subscribe(
        (shouldDisassemble: boolean) => {
          if (shouldDisassemble) {
            this.assemble();
          }
        }
      )
    );

    this.subscription.add(
      this.provisioning.confirmationFinished.subscribe(
        (allConfirmed: boolean) => {
          this.connectionError = false;
          this.provisioning.enableActions.emit(true);
          this.actionsEnabled = true;
          this.isFinishedConfirming = true;
          this.isConfirming = false;
          this.isFinishedWithError = !allConfirmed;
          if (!this.isFinishedWithError && this.isDisassembly()) {
            this.alldissociated = true;
          }
          if (!this.isFinishedWithError) {
            this.finishOperations();
          } else {
            this.provisioning.saveStep(ProvisionerService.confirmedErrorStep);
          }
        }
      )
    );

    this.subscription.add(
      this.provisioning.backButtonPressed.subscribe(() => {
        if (this.isAssembly()) {
          this.connectionError = false;

          if (this.devices.length > 0) {
            console.log(
              `Before is Flashing :${
                this.provisioning.getStep() === ProvisionerService.flashingStep
              }`
            );
            const dialogRef = this.dialog.open(ClearDeviceListDialogComponent, {
              disableClose: true,
              width: "690px",
              data: {
                isFlashing:
                  this.provisioning.getStep() ===
                  ProvisionerService.flashingStep
              },
              autoFocus: false
            });

            this.subscription.add(
              dialogRef.afterClosed().subscribe((result) => {
                if (result) {
                  console.log(
                    `After is Flashing :${
                      this.provisioning.getStep() ===
                      ProvisionerService.flashingStep
                    }`
                  );
                  if (
                    !(
                      this.provisioning.getStep() ===
                      ProvisionerService.flashingStep
                    )
                  ) {
                    this.reset();
                    this.provisioning.confirmBack(false);
                  } else {
                    this.provisioning.confirmBack(result);
                    this.provisioning.deleteContainerType();
                    this.provisioning.clearDeviceList();
                    this.provisioning.saveStep(ProvisionerService.flashingStep);
                  }
                } else {
                  this.provisioning.confirmBack(false);
                }
              })
            );
          } else {
            this.provisioning.deleteContainerType();
            this.provisioning.confirmBack(true);
          }
        } else {
          this.provisioning.confirmBack(true);
          this.provisioning.clearDeviceList();
        }
      })
    );

    this.provisioning.updateDeviceList();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    clearTimeout(this.timeoutId);
  }

  finishOperations() {
    this.isAssembling = false;
    this.timeoutId = setTimeout(() => {
      if (this.isFinishedConfirming) {
        this.reset();
      }
    }, 5000);
  }

  async assemble() {
    console.log("Assemble is Flashing :");

    this.devicesWebsocket = [];
    this.isConfirming = true;
    this.isFlashing = false;
    this.waitingWebsocket = true;
    this.connectionError = false;
    this.actionsEnabled = false;
    this.provisioning.enableActions.emit(false);

    if (this.isAssembly()) {
      this.provisioning.provisionDevices();
    } else if (this.isDisassembly()) {
      this.provisioning.unprovisionDevices();
    }
    console.log(`Assemble is Flashing :${this.isFlashing}`);
  }

  test() {
    console.log("confirm", this.devices);
    this.provisioning.saveStep(ProvisionerService.confirmedStep);
    this.waitingWebsocket = true;
    this.connectionError = false;
    this.isAssembling = false;
    this.isConfirming = true;
    this.provisioning.enableActions.emit(false);
    this.actionsEnabled = false;
    if (this.isAssembly()) {
      this.provisioning.confirmDevicesProvisioning();
    } else if (this.isDisassembly()) {
      if (this.alldissociated) {
        this.finishOperations();
      } else {
        this.provisioning.unprovisionDevices();
      }
    }
  }

  reset() {
    clearTimeout(this.timeoutId);
    if (this.isAssembly()) {
      this.connectionError = false;
      this.isFlashing = true;
      this.isAssembling = false;
      this.isConfirming = false;
      this.isFinishedConfirming = false;
      this.isFinishedWithError = false;
      this.alldissociated = false;
      this.provisioning.saveStep(ProvisionerService.flashingStep);
      this.provisioning.clearDeviceList();
    } else {
      this.provisioning.saveStep(ProvisionerService.flashingStep);
      this.provisioning.clearDeviceList();
      this.provisioning.backPressed();
    }
  }

  addDevice(device: Device) {
    // console.log("Device flash Add device", device, this.devices.length);

    this.connectionError = false;
    this.provisioning.enableActions.emit(false);
    this.actionsEnabled = false;

    if (this.isAssembly()) {
      this.provisioning.addDevice(device);
    } else if (this.isDisassembly()) {
      this.provisioning.addDeviceDissociation(device);
    }

    this.actionsEnabled = true;
  }

  deepClone(layers: any[]) {
    for (let layer of layers) {
      layer = { ...layer };
    }
  }

  isAssembly(): boolean {
    return this.listDeviceType === "Assembly";
  }

  isDisassembly(): boolean {
    return this.listDeviceType === "Disassembly";
  }
}
